import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaShoppingBag,
  FaShippingFast,
  FaCheckCircle,
  FaClock,
  FaSearch,
  FaEye,
  FaTimes,
  FaBox,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaBan,
  FaUndo,
  FaCamera,
} from "react-icons/fa";
import axios from "axios";
import MyContext from "../../../context/MyContext";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import Swal from "sweetalert2";
import "../../../assets/css/seller/orders/SellerOrders.css";
import SellerLayout from "../../../layouts/SellerLayout";
import { SellerOrdersPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const SellerOrders = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const [rejectingOrderId, setRejectingOrderId] = useState(null);
  const [updatingOrderId, setUpdatingOrderId]   = useState(null);
  const [markingReadyOrderId, setMarkingReadyOrderId] = useState(null);

  // Image-Verification Checkpoint 1 (Ready for Pickup) — packing image
  // ab inline hai Order Details modal ke andar hi, alag popup modal nahi
  const [packingImageFile, setPackingImageFile] = useState(null);
  const [packingImagePreview, setPackingImagePreview] = useState(null);
  const [packingImageOrderId, setPackingImageOrderId] = useState(null);
  const [packingImageError, setPackingImageError] = useState("");
  const [returnReceivedImageFile, setReturnReceivedImageFile] = useState(null);
  const [returnReceivedImagePreview, setReturnReceivedImagePreview] = useState(null);
  const [returnReceivedImageError, setReturnReceivedImageError] = useState("");
  const [confirmingReturnOrderId, setConfirmingReturnOrderId] = useState(null);

  const [totalOrders, setTotalOrders]         = useState(0);
  const [pendingOrders, setPendingOrders]     = useState(0);
  const [inTransitOrders, setInTransitOrders] = useState(0);
  const [deliveredOrders, setDeliveredOrders] = useState(0);

  const stats = [
    { icon: <FaShoppingBag />,  color: "blue",   label: "Total Orders", value: totalOrders.toString() },
    { icon: <FaClock />,        color: "orange", label: "Pending",      value: pendingOrders.toString() },
    { icon: <FaShippingFast />, color: "purple", label: "In Transit",   value: inTransitOrders.toString() },
    { icon: <FaCheckCircle />,  color: "green",  label: "Delivered",    value: deliveredOrders.toString() },
  ];

  // ══════════════════════════════════════════════════════════════════════════
  // API FUNCTION — viewAllOrders (GET + Bearer, no kmseller_id)
  // ══════════════════════════════════════════════════════════════════════════
  const viewAllOrders = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/seller/order/viewAllOrders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          setOrders(response.data.orders);
          setTotalOrders(response.data.totalOrders);
          setPendingOrders(response.data.pendingOrders);
          setInTransitOrders(response.data.inTransitOrders);
          setDeliveredOrders(response.data.deliveredOrders);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load orders." });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewAllOrders();
  }, [authToken]);

  // ══════════════════════════════════════════════════════════════════════════
  // REJECT ORDER
  // ══════════════════════════════════════════════════════════════════════════
  const rejectOrder = (orderId) => {
    setRejectingOrderId(orderId);
    const formData = new FormData();
    formData.append("order_id", orderId);

    axios
      .post(`${baseUrl}/seller/order/rejectOrder`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setRejectingOrderId(null);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          Swal.fire({ icon: "success", title: "Success", text: "Order rejected successfully!" });
          viewAllOrders();
          setShowDetailModal(false);
          setSelectedOrder(null);
        }
      })
      .catch((error) => {
        setRejectingOrderId(null);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to reject order." });
      });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // MARK READY FOR PICKUP
  // ══════════════════════════════════════════════════════════════════════════
  const markReadyForPickup = (orderId, imageFile) => {
    if (!imageFile) {
      setPackingImageError("Please attach a photo of the packed product first.");
      return;
    }
    setPackingImageError("");
    setMarkingReadyOrderId(orderId);
    const formData = new FormData();
    formData.append("order_id", orderId);
    formData.append("image", imageFile);

    axios
      .post(`${baseUrl}/seller/order/markReadyForPickup`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setMarkingReadyOrderId(null);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error || response.data.message });
        } else {
          Swal.fire({ icon: "success", title: "Success", text: "Order marked ready for pickup!" });
          setPackingImageFile(null);
          setPackingImagePreview(null);
          setPackingImageOrderId(null);
          setPackingImageError("");
          viewAllOrders();
          setShowDetailModal(false);
          setSelectedOrder(null);
        }
      })
      .catch((error) => {
        setMarkingReadyOrderId(null);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update order status." });
      });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // CONFIRM RETURN RECEIVED
  // Final checkpoint of the doorstep-reject return flow. Stock is
  // already restored (customer-reject ke waqt) — ye sirf evidence-
  // checkpoint hai, condition-on-receipt photo.
  // ══════════════════════════════════════════════════════════════════════════
  const confirmReturnReceived = (orderId, imageFile) => {
    if (!imageFile) {
      setReturnReceivedImageError("Please attach a photo of the returned product first.");
      return;
    }
    setReturnReceivedImageError("");
    setConfirmingReturnOrderId(orderId);
    const formData = new FormData();
    formData.append("order_id", orderId);
    formData.append("image", imageFile);

    axios
      .post(`${baseUrl}/seller/order/confirmReturnReceived`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setConfirmingReturnOrderId(null);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error || response.data.message });
        } else {
          Swal.fire({ icon: "success", title: "Success", text: "Return confirmed as received!" });
          setReturnReceivedImageFile(null);
          setReturnReceivedImagePreview(null);
          setReturnReceivedImageError("");
          viewAllOrders();
          setShowDetailModal(false);
          setSelectedOrder(null);
        }
      })
      .catch((error) => {
        setConfirmingReturnOrderId(null);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to confirm return." });
      });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // UPDATE ORDER STATUS
  // ══════════════════════════════════════════════════════════════════════════
  const updateOrderStatus = (orderId, newStatus) => {
    setUpdatingOrderId(orderId);
    const formData = new FormData();
    formData.append("order_id", orderId);
    formData.append("status", newStatus);

    axios
      .post(`${baseUrl}/seller/order/updateOrderStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setUpdatingOrderId(null);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          Swal.fire({ icon: "success", title: "Success", text: `Order marked as ${newStatus}!` });
          viewAllOrders();
          setShowDetailModal(false);
          setSelectedOrder(null);
        }
      })
      .catch((error) => {
        setUpdatingOrderId(null);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update order status." });
      });
  };

  const getStatusClass = (status) =>
    ({
      delivered:  "order-status-delivered",
      shipping:   "order-status-shipping",
      processing: "order-status-processing",
      pending:    "order-status-pending",
      cancelled:  "order-status-cancelled",
      shipped:    "order-status-shipping",
      returned:   "order-status-cancelled",
    })[status?.toLowerCase()] || "order-status-pending";

  const waitingForDriverOrders = orders.filter(
    (o) => !o.kmdeliveryman_id || o.kmdeliveryjob?.status === "cancelled"
  );

  const returnOrders = orders.filter((o) =>
    ["returned", "return_completed"].includes(o.kmdeliveryjob?.status?.toLowerCase())
  );

  // ── "returned" = driver ne product wapis le aaya, seller ka
  // confirmation abhi baaki hai. "return_completed" = seller confirm
  // kar chuka hai — action-needed sirf "returned" wale hain ──
  const pendingReturnOrders = returnOrders.filter(
    (o) => o.kmdeliveryjob?.status?.toLowerCase() === "returned"
  );

  const displayedOrders =
    activeTab === "waiting" ? waitingForDriverOrders :
    activeTab === "returns" ? returnOrders :
    orders;

  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentOrders = displayedOrders.slice(indexOfFirst, indexOfLast);

  const renderSkeletonRows = (columnCount) =>
    Array.from({ length: itemsPerPage > 6 ? 6 : itemsPerPage }).map((_, i) => (
      <tr key={`skeleton-${i}`} className="sel-order-skeleton-row">
        {Array.from({ length: columnCount }).map((__, j) => (
          <td key={j}>
            <div className="sel-skeleton-bar" />
          </td>
        ))}
      </tr>
    ));

  const getReturnStatusLabel = (order) => {
    const jobStatus = order.kmdeliveryjob?.status?.toLowerCase();
    if (jobStatus === "return_completed") return "Return Completed";
    return "Awaiting Your Confirmation";
  };

  const renderCustomerCell = (customer) => (
    <div className="sel-order-customer-cell">
      <div className="sel-order-customer-avatar">{getInitials(customer?.name)}</div>
      <div>
        <p className="sel-order-customer-name">{customer?.name}</p>
        <p className="sel-order-customer-email">{customer?.email}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <SellerLayout>
        <SellerOrdersPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="order-container">
        <Container fluid>

          <div className="order-header">
            <div>
              <h1>Order Management</h1>
              <p>Track and manage your orders</p>
            </div>
          </div>

          <Row className="order-stats g-3 g-lg-4">
            {stats.map((stat, i) => (
              <Col key={i} xs={12} sm={6} xl={3}>
                <div className="order-stat-card">
                  <div className={`order-stat-icon ${stat.color}`}>{stat.icon}</div>
                  <div className="order-stat-content">
                    <div className="order-stat-label">{stat.label}</div>
                    <div className="order-stat-value">{stat.value}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <div className="sel-order-tabs">
            <button
              className={`sel-order-tab ${activeTab === "all" ? "sel-order-tab--active" : ""}`}
              onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
            >
              All Orders
            </button>
            <button
              className={`sel-order-tab ${activeTab === "waiting" ? "sel-order-tab--active" : ""}`}
              onClick={() => { setActiveTab("waiting"); setCurrentPage(1); }}
            >
              Waiting for Driver
              {waitingForDriverOrders.length > 0 && (
                <span className="sel-order-tab-badge">{waitingForDriverOrders.length}</span>
              )}
            </button>
            <button
              className={`sel-order-tab ${activeTab === "returns" ? "sel-order-tab--active" : ""}`}
              onClick={() => { setActiveTab("returns"); setCurrentPage(1); }}
            >
              <FaUndo style={{ marginRight: 4 }} /> Returns
              {pendingReturnOrders.length > 0 && (
                <span className="sel-order-tab-badge sel-order-tab-badge-warning">
                  {pendingReturnOrders.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === "all" && (
            <div className="order-table-header">
              <div className="order-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="order-filters">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="order-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          )}

          <div className="order-table-section">
            <div className="order-table-wrapper">
              {activeTab === "returns" ? (
                <table className="order-table">
                  <thead>
                    <tr>
                      <th>ORDER ID</th>
                      <th>CUSTOMER</th>
                      <th>ITEMS</th>
                      <th>ORDER TOTAL</th>
                      <th>DELIVERY CHARGE (Pay to Driver)</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      renderSkeletonRows(7)
                    ) : (
                      <>
                        {currentOrders.map((order) => {
                          const charge = order.kmdeliveryjob?.final_delivery_charge;

                          return (
                            <tr key={order.id}>
                              <td className="order-id">{order.id}</td>
                              <td>{renderCustomerCell(order.kmcustomer)}</td>
                              <td>{order.kmcarts?.length || 0} item(s)</td>
                              <td className="order-amount">PKR {order.totalPrice?.toLocaleString()}</td>
                              <td className="order-amount" style={{ color: "#e53935", fontWeight: 700 }}>
                                {charge ? `PKR ${Number(charge).toLocaleString()}` : "—"}
                              </td>
                              <td>
                                <span
                                  className={`order-status ${
                                    getReturnStatusLabel(order) === "Return Completed"
                                      ? "order-status-delivered"
                                      : "order-status-cancelled"
                                  }`}
                                >
                                  {getReturnStatusLabel(order)}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="ksa-icon-action-btn"
                                  title="View Details"
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <FaEye />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {currentOrders.length === 0 && (
                          <tr>
                            <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                              No returned orders.
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="order-table">
                  <thead>
                    <tr>
                      <th>ORDER ID</th>
                      <th>CUSTOMER</th>
                      <th>ITEMS</th>
                      <th>TOTAL</th>
                      <th>DRIVER</th>
                      <th>STATUS</th>
                      <th>DATE</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      renderSkeletonRows(8)
                    ) : (
                      <>
                        {currentOrders.map((order) => {
                          const hasDriver = !!order.kmdeliveryman_id;

                          return (
                            <tr key={order.id}>
                              <td className="order-id">{order.id}</td>
                              <td>{renderCustomerCell(order.kmcustomer)}</td>
                              <td>{order.kmcarts?.length || 0} item(s)</td>
                              <td className="order-amount">PKR {order.totalPrice?.toLocaleString()}</td>
                              <td>
                                {hasDriver ? (
                                  <span style={{ color: "#097d43", fontWeight: 600, fontSize: 13 }}>
                                    {order.kmdeliveryman?.name}
                                  </span>
                                ) : (
                                  <span style={{ color: "#e53935", fontSize: 12, fontWeight: 600 }}>
                                    No Driver
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className={`order-status ${getStatusClass(order.status)}`}>
                                  {order.status}
                                </span>
                              </td>
                              <td>{order.created_at?.slice(0, 10)}</td>
                              <td>
                                <button
                                  className="ksa-icon-action-btn"
                                  title="View Details"
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <FaEye />
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {currentOrders.length === 0 && (
                          <tr>
                            <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                              {activeTab === "waiting" ? "No orders waiting for driver." : "No orders found."}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <Pagination
              totalItems={displayedOrders.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            />
          </div>
        </Container>
      </div>

      {showDetailModal && selectedOrder && (
        <div
          className="ksa-modal-overlay"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedOrder(null);
            setPackingImageFile(null);
            setPackingImagePreview(null);
            setPackingImageOrderId(null);
            setPackingImageError("");
          }}
        >
          <div
            className="ksa-modal ksa-modal-lg order-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ksa-subadmin-modal-header">
              <h3 className="ksa-subadmin-modal-title">Order Details</h3>
              <button
                className="ksa-modal-close"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedOrder(null);
                  setPackingImageFile(null);
                  setPackingImagePreview(null);
                  setPackingImageOrderId(null);
                  setPackingImageError("");
                }}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-subadmin-modal-body order-detail-body">
              {/* FIXED: "returned" yahan se hata diya — jab customer
                  doorstep par order reject karta hai, order.status khud
                  bhi "returned" ban jata hai (kmdeliveryjob.status ke
                  saath saath), jiski wajah se ye poora block hi render
                  nahi hota tha aur seller ko "Confirm Return Received"
                  ka upload button kabhi dikhta hi nahi tha. Andar ka
                  code pehle se hi kmdeliveryjob?.status ke hisaab se
                  sahi content dikhata hai (assigned → packing upload,
                  returned → return-confirm upload, pending → reject
                  button), is liye "returned" hatana safe hai. */}
              {selectedOrder.status?.toLowerCase() !== "delivered" &&
               selectedOrder.status?.toLowerCase() !== "cancelled" && (
                <div className="order-status-change">
                  <h5>Update Order Status:</h5>
                  <div className="order-status-buttons">
                    {selectedOrder.kmdeliveryjob?.status?.toLowerCase() === "assigned" && (
                      <div className="order-packing-upload" style={{ width: "100%", marginBottom: 12 }}>
                        {/* Packing photo mandatory hai — Admin dispute ke waqt isay review karega */}
                        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
                          <FaCamera style={{ marginRight: 6 }} />
                          Attach a photo of the packed product
                        </label>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setPackingImageFile(file);
                              setPackingImagePreview(URL.createObjectURL(file));
                              setPackingImageOrderId(selectedOrder.id);
                              setPackingImageError("");
                            }
                          }}
                          style={{
                            display: "block",
                            marginBottom: 8,
                            borderColor: packingImageError ? "#dc3545" : undefined,
                          }}
                        />

                        {packingImageError && (
                          <p style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>
                            {packingImageError}
                          </p>
                        )}

                        {packingImagePreview && (
                          <img
                            src={packingImagePreview}
                            alt="Packing preview"
                            style={{
                              width: 140,
                              height: 140,
                              objectFit: "cover",
                              borderRadius: 8,
                              marginBottom: 10,
                              display: "block",
                            }}
                          />
                        )}

                        <Button
                          variant="outline"
                          onClick={() => markReadyForPickup(selectedOrder.id, packingImageFile)}
                          disabled={markingReadyOrderId === selectedOrder.id || rejectingOrderId === selectedOrder.id || updatingOrderId === selectedOrder.id}
                        >
                          {markingReadyOrderId === selectedOrder.id ? "Updating..." : "Mark as Ready for Pickup"}
                        </Button>
                      </div>
                    )}

                    {/* ── NEW: Confirm Return Received — final checkpoint
                         of the doorstep-reject flow. Same pattern as the
                         packing-photo upload above. ── */}
                    {selectedOrder.kmdeliveryjob?.status?.toLowerCase() === "returned" && (
                      <div className="order-packing-upload" style={{ width: "100%", marginBottom: 12 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
                          <FaCamera style={{ marginRight: 6 }} />
                          Attach a photo of the returned product you received
                        </label>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setReturnReceivedImageFile(file);
                              setReturnReceivedImagePreview(URL.createObjectURL(file));
                              setReturnReceivedImageError("");
                            }
                          }}
                          style={{
                            display: "block",
                            marginBottom: 8,
                            borderColor: returnReceivedImageError ? "#dc3545" : undefined,
                          }}
                        />

                        {returnReceivedImageError && (
                          <p style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>
                            {returnReceivedImageError}
                          </p>
                        )}

                        {returnReceivedImagePreview && (
                          <img
                            src={returnReceivedImagePreview}
                            alt="Return received preview"
                            style={{
                              width: 140,
                              height: 140,
                              objectFit: "cover",
                              borderRadius: 8,
                              marginBottom: 10,
                              display: "block",
                            }}
                          />
                        )}

                        <Button
                          variant="outline"
                          onClick={() => confirmReturnReceived(selectedOrder.id, returnReceivedImageFile)}
                          disabled={confirmingReturnOrderId === selectedOrder.id}
                        >
                          {confirmingReturnOrderId === selectedOrder.id ? "Updating..." : "Confirm Return Received"}
                        </Button>
                      </div>
                    )}
                    {/* {selectedOrder.status?.toLowerCase() !== "shipping" && (
                      <Button
                        variant="outline"
                        onClick={() => updateOrderStatus(selectedOrder.id, "Shipping")}
                        disabled={updatingOrderId === selectedOrder.id || rejectingOrderId === selectedOrder.id}
                      >
                        {updatingOrderId === selectedOrder.id ? "Updating..." : "Mark as Shipping"}
                      </Button>
                    )} */}
                    {selectedOrder.status?.toLowerCase() === "pending" && (
                      <Button
                        variant="danger"
                        icon={<FaBan />}
                        onClick={() => rejectOrder(selectedOrder.id)}
                        disabled={rejectingOrderId === selectedOrder.id || updatingOrderId === selectedOrder.id}
                      >
                        {rejectingOrderId === selectedOrder.id ? "Rejecting..." : "Reject Order"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {["cancelled", "returned"].includes(selectedOrder.status?.toLowerCase()) && (
                <div className="order-return-notice">
                  <h6 style={{ display: "flex", alignItems: "center", gap: 6, color: "#e53935" }}>
                    <FaUndo /> Return Information
                  </h6>
                  <p style={{ fontSize: 13, margin: "6px 0" }}>
                    This order was rejected by the customer at delivery.{" "}
                    {getReturnStatusLabel(selectedOrder) === "Return Completed"
                      ? "You have confirmed receiving the product back."
                      : "The delivery driver is bringing the product back to you."}
                  </p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#e53935" }}>
                    Delivery charge to pay driver: PKR{" "}
                    {selectedOrder.kmdeliveryjob?.final_delivery_charge
                      ? Number(selectedOrder.kmdeliveryjob.final_delivery_charge).toLocaleString()
                      : "0"}
                  </p>
                </div>
              )}

              <div className="order-receipt">
                <div className="receipt-header">
                  <div>
                    <div className="receipt-order-id">Order #{selectedOrder.id}</div>
                    <div className="receipt-date">{selectedOrder.created_at?.slice(0, 10)}</div>
                  </div>
                  <span className={`order-status ${getStatusClass(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>

                <div className="receipt-section">
                  <h6><FaBox /> Customer &amp; Delivery</h6>
                  <div className="receipt-row"><span>Customer</span><strong>{selectedOrder.kmcustomer?.name}</strong></div>
                  <div className="receipt-row"><FaPhone /><span>{selectedOrder.kmcustomer?.phone}</span></div>
                  <div className="receipt-row"><FaEnvelope /><span>{selectedOrder.kmcustomer?.email}</span></div>
                  <div className="receipt-row"><FaMapMarkerAlt /><span>{selectedOrder.shippingAddress}</span></div>
                </div>

                <div className="receipt-section">
                  <h6><FaShippingFast /> Delivery</h6>
                  {selectedOrder.kmdeliveryman ? (
                    <>
                      <div className="receipt-row"><span>Driver</span><strong>{selectedOrder.kmdeliveryman.name}</strong></div>
                      <div className="receipt-row"><FaPhone /><span>{selectedOrder.kmdeliveryman.phone}</span></div>
                      <div className="receipt-row"><span>Vehicle</span><strong>{selectedOrder.kmdeliveryman.vechile_type} - {selectedOrder.kmdeliveryman.vehicle_number}</strong></div>
                      <div className="receipt-row">
                        <span>Delivery Status</span>
                        <span className={`order-status ${getStatusClass(selectedOrder.kmdeliveryjob?.status)}`}>
                          {selectedOrder.kmdeliveryjob?.status}
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="receipt-empty" style={{ color: "#e53935" }}>
                      ⚠️ Abhi tak koi delivery man assign nahi hua.
                    </p>
                  )}
                </div>

                <div className="receipt-section">
                  <h6><FaShoppingBag /> Products</h6>
                  {selectedOrder.kmcarts?.map((item, i) => (
                    <div className="receipt-product-line" key={i}>
                      <div className="receipt-product-name">
                        {item.kmproduct?.name}
                        <span className="receipt-product-qty">× {item.quantity} {item.kmproduct?.unit}</span>
                      </div>
                      <div className="receipt-product-price">PKR {item.kmproduct?.price?.toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                <div className="receipt-section receipt-totals">
                  <div className="receipt-row"><span>Sub Total</span><span>PKR {selectedOrder.subTotal?.toLocaleString()}</span></div>
                  <div className="receipt-row"><span>Discount</span><span>- PKR {selectedOrder.discount?.toLocaleString()}</span></div>
                  <div className="receipt-row receipt-grand-total"><span>Total</span><span>PKR {selectedOrder.totalPrice?.toLocaleString()}</span></div>
                  <div className="receipt-row"><span>Payment Method</span><strong>{selectedOrder.paymentType}</strong></div>
                </div>
              </div>
            </div>

            <div className="ksa-subadmin-modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedOrder(null);
                  setPackingImageFile(null);
                  setPackingImagePreview(null);
                  setPackingImageOrderId(null);
                  setPackingImageError("");
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </SellerLayout>
  );
};

export default SellerOrders;
